import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BacktestInterface = () => {
  const [formData, setFormData] = useState({
    instrumentId: '',
    startDate: '',
    endDate: '',
    initialCapital: 10000,
    transactionCost: 5,
    maShort: 20,
    maLong: 50
  });
  
  const [instruments, setInstruments] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch instruments when component mounts
  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        const response = await fetch('/instruments');
        if (!response.ok) throw new Error('Failed to fetch instruments');
        const data = await response.json();
        setInstruments(data);
      } catch (err) {
        setError('Failed to load instruments: ' + err.message);
      }
    };

    fetchInstruments();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
  
    try {
      const response = await fetch(`/instruments/${formData.instrumentId}/backtest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify(formData)
      });
  
      if (!response.ok) throw new Error('Failed to run backtest');
      
      const data = await response.json();
      console.log('DATA:', data);
      setResults(data);
        
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h2>Moving Average Crossover Strategy Backtest</h2>
        </div>
        <div className="card-content">
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="instrumentId">Stock</label>
                <select
                  id="instrumentId"
                  name="instrumentId"
                  value={formData.instrumentId}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="">Select a stock...</option>
                  {instruments && instruments.map(instrument => (
                    <option key={instrument.id} value={instrument.id}>
                      {instrument.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Other form fields remain the same */}
              
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="initialCapital">Initial Capital</label>
                <input
                  type="number"
                  id="initialCapital"
                  name="initialCapital"
                  value={formData.initialCapital}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="transactionCost">Transaction Cost</label>
                <input
                  type="number"
                  id="transactionCost"
                  name="transactionCost"
                  value={formData.transactionCost}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="maShort">Short MA Period</label>
                <input
                  type="number"
                  id="maShort"
                  name="maShort"
                  value={formData.maShort}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="maLong">Long MA Period</label>
                <input
                  type="number"
                  id="maLong"
                  name="maLong"
                  value={formData.maLong}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Running...' : 'Run Backtest'}
            </button>
          </form>
        </div>
      </div>

      {error && (
        <div className="alert error">
          {error}
        </div>
      )}

{results && (
  <div className="results">
    <div className="card">
      <div className="card-header">
        <h2>Performance Metrics</h2>
      </div>
      <div className="card-content">
  {results.performance_metrics && (
    <div className="metrics-table">
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {/* Loop through the keys of performance_metrics */}
          {Object.entries(results.performance_metrics).map(([key, value]) => (
            <tr key={key}>
              <td>{key.replace(/_/g, ' ').toUpperCase()}</td> {/* Format key names to readable format */}
              <td>{typeof value === 'number' ? value.toFixed(2) : value}</td> {/* Format numbers to 2 decimals */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</div>

    </div>

          <div className="card">
            <div className="card-header">
              <h2>Price and Moving Averages</h2>
            </div>
            <div className="card-content">
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={results.daily_positions}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="price" stroke="#8884d8" dot={false} />
                    <Line type="monotone" dataKey="ma_short" stroke="#82ca9d" dot={false} />
                    <Line type="monotone" dataKey="ma_long" stroke="#ffc658" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BacktestInterface;